import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    adminListAssignmentsOfCustomerQuiz,
    getCsvMarksheetOfCustomerQuizV2,
    getUserQuizDetails,
    postCustomerQuizzesV2List,
} from '@/features/Quiz/api/getQuizzes';
import { Box, Button, Flex, Select, Spinner, Table, Tbody, Td, Text, Thead, Tr, useDisclosure } from '@chakra-ui/react';
import { ThEl } from '@/features/Quiz/components/ArticlesQuiz';
import { format } from 'date-fns';
import Icon from '@/components/Icon';
import { CustomerQuizHeaderCard } from '@/features/Quiz/components/CustomerQuizHeaderCard';
import { GradeSyncLogsModal } from '@/features/Quiz/components/GradeSyncLogsModal';
import type { GradeSyncLogsModalContext } from '@/features/Quiz/components/GradeSyncLogsModal';
import { useNotificationStore } from '@/stores/notifications';
import type { AdminQuizDetailsContent } from '@/features/Quiz/utils/quizV2AdminEdit';
import type { QuizV2AssignmentSummaryItem, QuizV2AssignmentAttemptRow } from '@/features/Quiz/types';
import { getQuizV2BasePath } from '@/features/Quiz/utils/quizV2BasePath';

const ASSIGNMENTS_PAGE_SIZE = 20;

function flattenAssignmentRows(items: QuizV2AssignmentSummaryItem[]): {
    rowKey: string;
    assignment: QuizV2AssignmentSummaryItem;
    attempt: QuizV2AssignmentAttemptRow | null;
}[] {
    const out: {
        rowKey: string;
        assignment: QuizV2AssignmentSummaryItem;
        attempt: QuizV2AssignmentAttemptRow | null;
    }[] = [];
    for (const assignment of items) {
        const attempts = assignment.attempts ?? [];
        if (attempts.length === 0) {
            out.push({
                rowKey: `${assignment.assignmentInfo?.assignmentUuid ?? 'a'}-none`,
                assignment,
                attempt: null,
            });
        } else {
            for (const att of attempts) {
                out.push({
                    rowKey: att.attemptUuid,
                    assignment,
                    attempt: att,
                });
            }
        }
    }
    return out;
}

type AssignmentFlatRow = {
    rowKey: string;
    assignment: QuizV2AssignmentSummaryItem;
    attempt: QuizV2AssignmentAttemptRow | null;
};

type AssignmentsTableProps = {
    flatRows: AssignmentFlatRow[];
    totalPages: number;
    assignmentsPage: number;
    setAssignmentsPage: React.Dispatch<React.SetStateAction<number>>;
    onOpenGradeSyncLogs: (context: GradeSyncLogsModalContext) => void;
};

function AssignmentsTable({
    flatRows,
    totalPages,
    assignmentsPage,
    setAssignmentsPage,
    onOpenGradeSyncLogs,
}: AssignmentsTableProps) {
    const { pathname } = useLocation();
    const basePath = getQuizV2BasePath(pathname);
    return (
        <>
            <Table bgColor={'#ffffff'}>
                <Thead>
                    <Tr>
                        <ThEl align="start" w="22%">
                            Student
                        </ThEl>
                        <ThEl w="14%" align="start">
                            Class / source
                        </ThEl>
                        <ThEl w="8%">Attempt</ThEl>
                        <ThEl w="10%">Score</ThEl>
                        <ThEl w="10%">Percentage</ThEl>
                        <ThEl w="16%">Submitted</ThEl>
                        <ThEl w="24%">Actions</ThEl>
                    </Tr>
                </Thead>
                <Tbody>
                    {flatRows.map(({ rowKey, assignment, attempt }) => {
                        const email =
                            assignment.user?.email?.trim() ||
                            [assignment.user?.firstName, assignment.user?.lastName].filter(Boolean).join(' ').trim() ||
                            '—';
                        const classLabel =
                            assignment.class?.name ??
                            (assignment.assignmentInfo?.sourceType === 'MY_CLASS'
                                ? `Class id ${assignment.assignmentInfo?.sourceIdentifier ?? '—'}`
                                : (assignment.assignmentInfo?.sourceType ?? '—'));
                        const pct =
                            attempt?.scorePercentage != null ? Number(attempt.scorePercentage).toFixed(1) : null;
                        const scoreParts =
                            attempt?.obtainedScore != null && attempt?.maxScore != null
                                ? `${attempt.obtainedScore} / ${attempt.maxScore}`
                                : null;

                        return (
                            <Tr key={rowKey}>
                                <Td>{email}</Td>
                                <Td>{classLabel}</Td>
                                <Td>{attempt?.attemptNumber ?? '—'}</Td>
                                <Td>{scoreParts ?? '—'}</Td>
                                <Td>{pct != null ? `${pct}%` : '—'}</Td>
                                <Td>
                                    {attempt?.submittedAt
                                        ? format(new Date(attempt.submittedAt), 'MM/dd/yyyy  hh:mm  a')
                                        : '—'}
                                </Td>
                                <Td>
                                    {attempt?.attemptUuid ? (
                                        <Flex direction="column" gap={2} align="flex-start">
                                            <Button
                                                variant="outline"
                                                color="quiz.primaryBlue"
                                                fontSize="14px"
                                                fontWeight="500"
                                                h="32px"
                                                p="8px 30px"
                                                borderRadius="40px"
                                                borderColor="quiz.primaryBlue"
                                                as={Link}
                                                to={`${basePath}/attempt/${attempt.attemptUuid}`}>
                                                View detailed attempt
                                            </Button>
                                            {assignment.assignmentInfo?.sourceType === 'LTI' && (
                                                <Button
                                                    variant="outline"
                                                    color="quiz.primaryBlue"
                                                    fontSize="14px"
                                                    fontWeight="500"
                                                    h="32px"
                                                    p="8px 16px"
                                                    borderRadius="40px"
                                                    borderColor="quiz.primaryBlue"
                                                    onClick={() =>
                                                        onOpenGradeSyncLogs({
                                                            attemptUuid: attempt.attemptUuid,
                                                            quizTitle: '—',
                                                            attemptNumber: attempt.attemptNumber,
                                                            score: scoreParts,
                                                            percentage: pct,
                                                            fallbackEmail: email !== '—' ? email : null,
                                                        })
                                                    }>
                                                    View Grade Sync Logs
                                                </Button>
                                            )}
                                        </Flex>
                                    ) : (
                                        '—'
                                    )}
                                </Td>
                            </Tr>
                        );
                    })}
                </Tbody>
            </Table>
            {totalPages > 1 && (
                <Flex justifyContent="flex-end" gap={2} mt={4} alignItems="center">
                    <Button
                        size="sm"
                        variant="outline"
                        isDisabled={assignmentsPage <= 0}
                        onClick={() => setAssignmentsPage((p) => Math.max(0, p - 1))}>
                        Previous
                    </Button>
                    <Text fontSize="sm">
                        Page {assignmentsPage + 1} of {totalPages}
                    </Text>
                    <Button
                        size="sm"
                        variant="outline"
                        isDisabled={assignmentsPage >= totalPages - 1}
                        onClick={() => setAssignmentsPage((p) => p + 1)}>
                        Next
                    </Button>
                </Flex>
            )}
        </>
    );
}

export const CustomerResultsV2 = () => {
    const { quizUuid } = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const basePath = getQuizV2BasePath(pathname);
    const { addNotification } = useNotificationStore();

    const [assignmentsPage, setAssignmentsPage] = useState(0);
    const [downloading, setDownloading] = useState(false);
    const [gradeSyncModalContext, setGradeSyncModalContext] = useState<GradeSyncLogsModalContext | null>(null);
    const { isOpen: isGradeSyncModalOpen, onOpen: onGradeSyncModalOpen, onClose: onGradeSyncModalClose } = useDisclosure();

    const { data: quizDetails, isLoading: quizLoading } = useQuery(
        ['getUserQuizDetails-results-v2', quizUuid],
        () => (quizUuid ? getUserQuizDetails(quizUuid) : null),
        { enabled: !!quizUuid, useErrorBoundary: (error: { response?: unknown }) => Boolean(error.response) }
    );

    const { data: assignmentsData, isLoading: assignmentsLoading } = useQuery(
        ['customer-quiz-v2-assignments', quizUuid, assignmentsPage],
        () =>
            quizUuid
                ? adminListAssignmentsOfCustomerQuiz(quizUuid, {
                      page: assignmentsPage,
                      size: ASSIGNMENTS_PAGE_SIZE,
                  })
                : null,
        {
            enabled: !!quizUuid,
            keepPreviousData: true,
            useErrorBoundary: (error: { response?: unknown }) => Boolean(error.response),
        }
    );

    const { data: quizzesPicker } = useQuery(
        ['customer-quizzes-v2-results-picker'],
        () =>
            postCustomerQuizzesV2List({
                page: 0,
                size: 200,
                sort: [['startTime', 'desc']],
                showExpired: true,
            }),
        { useErrorBoundary: false }
    );

    const quizMeta = (quizDetails as AdminQuizDetailsContent | undefined)?.quiz;
    const isGraded = Boolean(quizMeta?.evaluationType && quizMeta.evaluationType !== 'UNGRADED');

    const handleOpenGradeSyncLogs = useCallback(
        (context: GradeSyncLogsModalContext) => {
            setGradeSyncModalContext({
                ...context,
                quizTitle: quizMeta?.title ?? context.quizTitle,
            });
            onGradeSyncModalOpen();
        },
        [onGradeSyncModalOpen, quizMeta?.title]
    );

    const flatRows = useMemo(
        () => flattenAssignmentRows(assignmentsData?.data ?? []),
        [assignmentsData?.data]
    );

    const completedCount = useMemo(() => {
        let n = 0;
        for (const item of assignmentsData?.data ?? []) {
            for (const a of item.attempts ?? []) {
                if (a.status === 'SUBMITTED' || a.submittedAt) n += 1;
            }
        }
        return n;
    }, [assignmentsData?.data]);

    const handleQuizChange = useCallback(
        (e: ChangeEvent<HTMLSelectElement>) => {
            const next = e.target.value;
            if (next) {
                navigate(`${basePath}/results/${next}`);
                setAssignmentsPage(0);
            }
        },
        [navigate, basePath]
    );

    const handleDownload = useCallback(async () => {
        if (!quizUuid) return;
        setDownloading(true);
        try {
            await getCsvMarksheetOfCustomerQuizV2(quizUuid);
        } catch (e: unknown) {
            const status =
                e && typeof e === 'object' && 'response' in e
                    ? (e as { response?: { status?: number } }).response?.status
                    : undefined;
            const message =
                e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : '';
            addNotification({
                type: 'error',
                title:
                    status === 410
                        ? 'Quiz not assigned yet — nothing to download.'
                        : message || 'Failed to download CSV.',
            });
        } finally {
            setDownloading(false);
        }
    }, [quizUuid, addNotification]);

    const totalAssignments = assignmentsData?.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalAssignments / ASSIGNMENTS_PAGE_SIZE));

    const pickerOptions = useMemo(() => {
        const map = new Map<string, string>();
        for (const r of quizzesPicker?.data ?? []) {
            const u = r.uuid ?? r.quizUuid ?? '';
            if (u) map.set(u, r.title);
        }
        if (quizUuid && quizMeta?.title && !map.has(quizUuid)) {
            map.set(quizUuid, quizMeta.title);
        }
        return [...map.entries()].map(([uuid, t]) => ({ uuid, title: t }));
    }, [quizzesPicker?.data, quizUuid, quizMeta]);

    const showDownload = totalAssignments > 0;

    return (
        <>
            <Box mb={'20px'} backgroundColor="#ffffff" p={'20px'}>
                <Flex alignItems={'center'} fontSize={'18px'}>
                    <Button variant="back" onClick={() => navigate(-1)} leftIcon={<Icon icon="chevron-left" />} />
                    <Text fontSize="20px" fontWeight="500" color="#202020" p="0">
                        Quiz Results
                    </Text>
                </Flex>

                <Select
                    h="50px"
                    w="366px"
                    border="1px solid #CDCDCD"
                    borderRadius="40px"
                    mt="20px"
                    fontSize="16px"
                    fontWeight="500"
                    icon={<Icon style={{ marginLeft: '10px' }} icon="chevron-down" />}
                    iconSize="14px 16px"
                    value={quizUuid ?? ''}
                    onChange={handleQuizChange}
                    className="quiz_select"
                    isDisabled={quizLoading}>
                    {pickerOptions.map((item) => (
                        <option key={item.uuid} value={item.uuid}>
                            {item.title}
                        </option>
                    ))}
                </Select>

                <Flex py={'20px'} justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={4}>
                    <Flex gap="31px" flexWrap="wrap">
                        <CustomerQuizHeaderCard
                            icon="question-square"
                            title="Quiz Type"
                            value={isGraded ? 'Graded' : 'Ungraded'}
                        />
                        <Box border="1px solid #EDEDED" />
                        <CustomerQuizHeaderCard
                            icon="clock"
                            title="Deadline"
                            value={
                                quizMeta?.endTime
                                    ? format(new Date(quizMeta.endTime), 'MM/dd/yyyy hh:mm a')
                                    : 'MM/DD/YYYY HH:MM'
                            }
                        />
                        <Box border="1px solid #EDEDED" />
                        <CustomerQuizHeaderCard
                            icon="share-from-square"
                            title="Distributed to"
                            value={assignmentsLoading ? '-' : String(totalAssignments)}
                        />
                        <Box border="1px solid #EDEDED" />
                        <CustomerQuizHeaderCard
                            icon="check-circle"
                            title="Completed attempts"
                            value={assignmentsLoading ? '-' : String(completedCount)}
                        />
                    </Flex>

                    <Button
                        variant={'brand'}
                        w="150px"
                        onClick={handleDownload}
                        disabled={!showDownload || downloading}>
                        {downloading ? <Spinner /> : 'Download CSV'}
                    </Button>
                </Flex>
            </Box>

            {assignmentsLoading ? (
                <Spinner size={'lg'} />
            ) : flatRows.length > 0 ? (
                <AssignmentsTable
                    flatRows={flatRows}
                    totalPages={totalPages}
                    assignmentsPage={assignmentsPage}
                    setAssignmentsPage={setAssignmentsPage}
                    onOpenGradeSyncLogs={handleOpenGradeSyncLogs}
                />
            ) : (
                <Text fontSize="18px" bg="#edf2f7" textAlign="center" py="16px" mt="70px">
                    Distribute quiz before viewing results
                </Text>
            )}
            <GradeSyncLogsModal
                isOpen={isGradeSyncModalOpen}
                onClose={onGradeSyncModalClose}
                context={gradeSyncModalContext}
            />
        </>
    );
};
